import { Button, GridCellInput, TOUCH } from "@nymbl/nos2-rules";
import { useId } from "react";
import type { GridCell as GridCellModel } from "@/lib/time/grid";
import { formatHours, LOCK_LABELS } from "@/lib/time/rules";
import { cn } from "@/lib/utils";

interface GridCellProps {
  cell: GridCellModel;
  /** The cell's place in the grid, for keyboard movement. */
  row: number;
  col: number;
  /** "App Gen, Wed 14, hours". */
  label: string;
  /** The text of a value that did not save, shown as typed with "Not saved". */
  pendingRaw: string | null;
  onCommit: (raw: string) => void;
  onOpenEntries: () => void;
}

/**
 * One task-day. `GridCellInput` shows `value` again on every blur, so a value that did not save
 * goes in as `value` and stays in its cell. Remount with a key that includes the saved value, so a
 * refetch resets it.
 */
export function GridCell({
  cell,
  row,
  col,
  label,
  pendingRaw,
  onCommit,
  onOpenEntries,
}: GridCellProps) {
  const errorId = useId();
  const saved = cell.hours > 0 ? formatHours(cell.hours) : "";
  // A held value, or a stored billable entry of 2 hrs or more with no comment (not counted).
  const notSaved = pendingRaw !== null || cell.notSaved;

  if (cell.mode === "multiple") {
    return (
      <Button
        variant="ghost"
        className={cn("w-full gap-1", TOUCH)}
        aria-label={`${label}, ${cell.entries.length} entries`}
        title={`${cell.entries.length} entries`}
        onClick={onOpenEntries}
      >
        {saved}
        <span className="text-xs text-muted-foreground">({cell.entries.length})</span>
      </Button>
    );
  }
  if (!cell.editable) {
    return (
      <div
        className="flex min-h-11 items-center justify-center text-sm font-semibold text-muted-foreground md:min-h-9"
        title={cell.lock ? LOCK_LABELS[cell.lock] : undefined}
      >
        {saved || "–"}
      </div>
    );
  }
  return (
    <div className="flex flex-col items-center gap-1">
      <GridCellInput
        row={row}
        col={col}
        value={pendingRaw ?? saved}
        onCommit={onCommit}
        retryOnBlur={pendingRaw !== null}
        aria-label={label}
        aria-invalid={notSaved || undefined}
        aria-describedby={notSaved ? errorId : undefined}
        placeholder="–"
        className="h-11 w-16 px-1 text-center font-semibold md:h-9"
      />
      {notSaved && (
        <span id={errorId} className="whitespace-nowrap text-xs font-semibold text-danger">
          Not saved
        </span>
      )}
    </div>
  );
}
